import config from './seo-config'
import { NEWS_PAGE_SIZE, newsPageItems } from './news-pagination'
import { faqItems } from './content'
import { pricingStages } from './pages/Pricing'
import newsArticles from './news-articles.json'

type SchemaNode = { '@type': string; '@id': string; [property: string]: unknown }
export const structuredDataId = 'site-structured-data'

export function buildStructuredData(pathname: string) {
  const metadata = (config.pages as Record<string, { title: string; description: string }>)[pathname]
  if (!metadata) return null
  const origin = config.origin
  const url = origin + pathname
  const organizationId = `${origin}/#organization`
  const websiteId = `${origin}/#website`
  const page: SchemaNode = {
    '@type': pathname === '/faq' ? 'FAQPage' : pathname === '/about' ? 'AboutPage' : pathname === '/enquiry' ? 'ContactPage' : 'WebPage',
    '@id': `${url}#webpage`, url, name: metadata.title, description: metadata.description,
    inLanguage: 'en', isPartOf: { '@id': websiteId }, publisher: { '@id': organizationId },
  }
  const graph: SchemaNode[] = [
    {
      '@type': 'Organization', '@id': organizationId, name: 'China Care', url: `${origin}/`,
      description: 'China Care is an independent coordination service for people considering non-emergency medical care in China.',
      logo: { '@type': 'ImageObject', url: `${origin}/brand/china-care-embrace.png`, width: 1024, height: 941 },
      email: 'info@chinacare.cc',
    },
    {
      '@type': 'WebSite', '@id': websiteId, name: 'China Care', url: `${origin}/`,
      inLanguage: 'en', publisher: { '@id': organizationId },
    },
    page,
  ]
  if (pathname === '/about' || pathname === '/') page.about = { '@id': organizationId }
  if (pathname === '/news' || /^\/news\/page\/[1-9][0-9]*$/.test(pathname)) {
    const pageNumber = pathname === '/news' ? 1 : Number(pathname.split('/').at(-1))
    const stories = newsPageItems(newsArticles, pageNumber)
    page['@type'] = 'CollectionPage'
    const list: SchemaNode = {
      '@type': 'ItemList', '@id': `${url}#stories`, name: 'International patients and medical expertise in China',
      numberOfItems: stories.length,
      itemListElement: stories.map((story, index) => ({ '@type': 'ListItem', position: (pageNumber - 1) * NEWS_PAGE_SIZE + index + 1, name: story.title, url: `${origin}/news/${story.slug}` })),
    }
    page.mainEntity = { '@id': list['@id'] }
    graph.push(list)
  }
  const story = newsArticles.find(item => pathname === `/news/${item.slug}`)
  if (story) {
    const article: SchemaNode = {
      '@type': 'NewsArticle', '@id': `${url}#article`, url,
      headline: story.title, description: story.description, inLanguage: 'en',
      datePublished: story.published, dateModified: story.published,
      author: { '@type': 'Organization', '@id': `${origin}/#editorial`, name: 'China Care Editorial', url: `${origin}/news` },
      publisher: { '@id': organizationId }, mainEntityOfPage: { '@id': page['@id'] },
      articleSection: story.category, citation: story.sources.map(source => source.url),
      articleBody: [story.lead, ...story.sections.flatMap(section => section.paragraphs)].join('\n\n'),
    }
    const breadcrumbs: SchemaNode = {
      '@type': 'BreadcrumbList', '@id': `${url}#breadcrumbs`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'China Care', item: `${origin}/` },
        { '@type': 'ListItem', position: 2, name: 'News', item: `${origin}/news` },
        { '@type': 'ListItem', position: 3, name: story.title, item: url },
      ],
    }
    page.mainEntity = { '@id': article['@id'] }
    page.breadcrumb = { '@id': breadcrumbs['@id'] }
    graph.push(article, breadcrumbs)
  }
  if (pathname === '/faq') {
    page.mainEntity = faqItems.map(([question, answer], index) => ({
      '@type': 'Question', '@id': `${url}#faq-question-${index + 1}`, name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    }))
  }
  if (pathname === '/how-we-help') {
    const service: SchemaNode = {
      '@type': 'Service', '@id': `${url}#patient-coordination`, name: 'Patient coordination in China',
      url, description: metadata.description, serviceType: 'Non-emergency patient coordination',
      provider: { '@id': organizationId },
    }
    graph.push(service)
    page.mainEntity = { '@id': service['@id'] }
  }
  if (pathname === '/pricing') {
    const services = pricingStages.map((stage): SchemaNode => {
      const consultation = stage.title === 'Specialist Consultation'
      const service: SchemaNode = {
        '@type': 'Service', '@id': `${url}#${stage.title.toLowerCase().replaceAll(' ', '-')}`,
        name: consultation ? 'Specialist consultation coordination' : stage.title,
        url, provider: { '@id': organizationId },
        description: (consultation ? 'Coordination for a remote consultation provided by a licensed doctor. ' : '') + stage.details[0] + ' ' + stage.details[1],
      }
      // Read the currency and amount from the same value displayed in the fee table.
      const price = stage.price.match(/^([A-Z]{3}) ([0-9]+(?:\.[0-9]+)?)$/)
      if (price) service.offers = {
        '@type': 'Offer', url, priceCurrency: price[1], price: price[2],
        description: `${stage.unit}. ${stage.details[1]}`, seller: { '@id': organizationId },
      }
      return service
    })
    graph.push(...services)
    page.mainEntity = services.map(service => ({ '@id': service['@id'] }))
  }
  return { '@context': 'https://schema.org', '@graph': graph }
}

export function serializeStructuredData(data: unknown): string {
  return JSON.stringify(data).replace(/[<>&\u2028\u2029]/g, character => `\\u${character.charCodeAt(0).toString(16).padStart(4, '0')}`)
}

export function syncStructuredData(pathname: string) {
  const data = buildStructuredData(pathname)
  let script = document.getElementById(structuredDataId)
  if (!data) { script?.remove(); return }
  if (!script) {
    script = document.createElement('script')
    script.id = structuredDataId
    script.setAttribute('type', 'application/ld+json')
    document.head.appendChild(script)
  }
  script.textContent = serializeStructuredData(data)
}
